import type { FlexProps } from "@qws-org/ui-kit";
import { Flex, Skeleton, Title, Typography } from "@qws-org/ui-kit";
import type { FC } from "react";
import { useEffect, useState } from "react";

import type { Schedule as ScheduleData, TransformerSchedule } from "~/core";

import { ScheduleTable } from "./components/ScheduleTable";

export type ScheduleProps = {
  data: ScheduleData;
  fetchSpreadsheet: (data: ScheduleData) => Promise<TransformerSchedule>;
} & FlexProps;

export const Schedule: FC<ScheduleProps> = ({
  data,
  fetchSpreadsheet,
  ...rest
}) => {
  const [spreadsheet, setSpreadsheet] = useState<TransformerSchedule | null>(
    null,
  );

  useEffect(() => {
    fetchSpreadsheet(data)
      .then((res) => {
        setSpreadsheet(res);
      })
      .catch((e) => {
        console.error(e);
      });
  }, [setSpreadsheet, fetchSpreadsheet]);

  return (
    <Flex
      flexDirection="column"
      align="center"
      p={{ py: 40, px: 15 }}
      {...rest}
      md={{ p: { py: 80 }, ...rest.md }}
      data-testid="schedule"
    >
      {data?.title && (
        <Title
          as="h2"
          fontWeight="mobile.bold"
          size="small"
          horizontalAlign="center"
          md={{
            fontWeight: "desktop.semibold",
            lineHeight: "title.desktop.large",
            fontSize: "title.desktop.large",
          }}
          data-testid="schedule-title"
        >
          {data.title}
        </Title>
      )}
      {data?.description && (
        <Typography
          m={{ mt: 8, mb: 24 }}
          horizontalAlign="center"
          color="text-accent-default"
          size="medium"
          data-testid="schedule-description"
        >
          {data.description}
        </Typography>
      )}

      {spreadsheet ? (
        <ScheduleTable spreadsheet={spreadsheet} />
      ) : (
        <Skeleton radius={4} height={470} width="100%" md={{ maxWidth: 700 }} />
      )}
    </Flex>
  );
};
